import {
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { SubmitButton } from '@tassat/button';
import { AssetTypes, SelectOption, assetPurposeTypeCharMap } from '@tassat/common-definitions';
import { AccountRecord, Asset, Transaction } from '@tassat/fred-definitions';
import { ButtonType, WalletAddress } from '@tassat/text';
import { convertToNumber, formatAsAsset, formatAsAssetNoSign } from '@tassat/utility';
import React from 'react';

import { registerTimeout } from 'Lib/serverTimeout';
import { formatEpochDate } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { sendTimeoutAction } from 'Reducers/actions/validationActions';
import { useAppDispatch } from 'Reducers/store/hooks';

import { ISendFormValues } from '../overlays/send/sendForm/types';

interface ISendConfirmationProps extends WithStyles<typeof muiStyles> {
    sendValues: ISendFormValues | Transaction;
    currentWallet: AccountRecord;
    handleSend: (sendValues) => void;
    cancelConfirmation: () => void;
    asset: Asset;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: { padding: '0 5rem' },
    contentDivider: {
        marginTop: '.5rem',
        marginBottom: '.3rem'
    },
    amountText: {
        padding: '.5rem',
        paddingBottom: '1.5rem'
    },
    disclaimerContainer: { paddingTop: '1.5rem' },
    disclaimerText: { flexBasis: '33%' },
    paymentDetailsContainer: {},
    table: {
        borderCollapse: 'separate' as const,
        borderSpacing: '.5rem'
    },
    tableRow: { marginBottom: '.3rem' },
    tableLabelCell: {
        backgroundColor: theme?.app?.applicationColors.tableRowLabelColor ||
            'rgba(185, 217, 236,5)',
        fontWeight: 'bold' as const
    },
    tableCell: {
        border: 'none' as const,
        paddingTop: '.3rem',
        paddingBottom: '.3rem'
    },
    submitContainer: { paddingTop: '2.5rem' },
    cancelButton: {
        cursor: 'pointer' as const,
        fontSize: '1.2rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        padding: '.3rem',
        marginTop: '1rem',
        '&:hover': {
            backgroundColor: theme?.app?.clientTheme.colors.buttons.primaryHover ||
                '#bec4cf'
        }
    }
});

export function SendConfirmation(props: ISendConfirmationProps) {
    const {
        classes,
        currentWallet,
        sendValues,
        handleSend,
        cancelConfirmation,
        asset
    } = props;

    const dispatch = useAppDispatch();
    const onTimeoutError = () => dispatch(sendTimeoutAction);

    const fromApproval = React.useMemo(() => (sendValues as Transaction).RequestId, [sendValues]);
    const assetObj = asset ??
        ((sendValues as ISendFormValues).AssetType?.refData?.security || (sendValues as ISendFormValues).AssetType?.refData?.currency);

    const {
        recipientName,
        recipientAddress,
        recipientFirmName,
        ticker,
        purpose,
        units,
        description
    } = React.useMemo(() => {
        if (!fromApproval && (sendValues as ISendFormValues).Recipient) {
            return {
                recipientName: (sendValues as ISendFormValues).Recipient.label,
                recipientAddress: (sendValues as ISendFormValues).Recipient.value.Account,
                recipientFirmName: (sendValues as ISendFormValues).Recipient.value.FirmName,
                ticker: (sendValues as ISendFormValues).AssetType.value,
                units: (sendValues as ISendFormValues).AssetType.refData?.security?.Units,
                description: (sendValues as ISendFormValues).AssetType.refData?.security?.Description,
                purpose: sendValues.Purpose ? (sendValues.Purpose as SelectOption).label : '-'
            };
        } else if (fromApproval) {
            return {
                recipientName: (sendValues as Transaction).RecipientFirm,
                recipientAddress: (sendValues as Transaction).RecipientAddress,
                recipientFirmName: (sendValues as Transaction).RecipientFirm,
                ticker: (sendValues as Transaction).Ticker,
                units: assetObj.Units,
                description: assetObj.Description,
                purpose: assetPurposeTypeCharMap((sendValues as Transaction).Purpose)
            };
        } else if ((sendValues as ISendFormValues).RecipientAddress) {
            return {
                recipientName: (sendValues as ISendFormValues).RecipientAccountName,
                recipientAddress: (sendValues as ISendFormValues).RecipientAddress,
                recipientFirmName: (sendValues as ISendFormValues).RecipientFirmName,
                ticker: (sendValues as ISendFormValues).AssetType.value,
                units: (sendValues as ISendFormValues).AssetType.refData?.security?.Units,
                description: (sendValues as ISendFormValues).AssetType.refData?.security?.Description,
                purpose: sendValues.Purpose ? (sendValues.Purpose as SelectOption).label : '-'
            };
        } else {
            return {
                recipientName: '',
                recipientAddress: '',
                recipientFirmName: '',
                ticker: '',
                purpose: '',
                units: '',
                description: ''
            };
        }
    }, [ sendValues, fromApproval ]);

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Typography variant='body1'>You are about to send</Typography>
                <Typography
                    className={classes.amountText}
                    variant='h3'
                >{assetObj.Type === AssetTypes.ASSET ?
                        fromApproval ?
                            `${formatAsAssetNoSign(convertToNumber((sendValues as Transaction).AssetQuantity), asset, true)} ${assetObj.Units} of ${assetObj.Ticker}`
                            : `${formatAsAssetNoSign(convertToNumber((sendValues as ISendFormValues).AssetAmount), (sendValues as ISendFormValues).AssetType.refData?.security, true)} ${(sendValues as ISendFormValues).AssetType.refData?.security.Units} of ${(sendValues as ISendFormValues).AssetType.refData?.security.Ticker}`
                        : fromApproval ?
                            formatAsAsset((sendValues as Transaction).AssetQuantity, asset, 'symbol')
                            : formatAsAsset((sendValues as Transaction).FiatAmount, (sendValues as ISendFormValues).AssetType.refData?.currency, 'symbol')}
                </Typography>
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                container
                direction='row'
                item
                spacing={0}
            >
                <Grid
                    className={classes.paymentDetailsContainer}
                    container
                    direction='column'
                    item
                    lg={6}
                    md={12}
                    spacing={0}
                    wrap='nowrap'
                >
                    <Table className={classes.table}>
                        <TableHead />
                        <TableBody>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Sender’s Lynq Account Name</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {`${fromApproval ? (sendValues as Transaction).SenderAccountName : (sendValues as ISendFormValues).Sender.AccountName}`}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Recipient’s Lynq Account Name</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {recipientName ?
                                        recipientName
                                        : (
                                            <WalletAddress
                                                buttonType={ButtonType.SMALL}
                                                isAdmin={false}
                                                walletAddress={recipientAddress}
                                            />
                                        )}
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Recipient Account ID</TableCell>
                                <TableCell className={classes.tableCell}>
                                    <WalletAddress
                                        buttonType={ButtonType.SMALL}
                                        isAdmin={false}
                                        walletAddress={recipientAddress}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Purpose</TableCell>
                                <TableCell className={classes.tableCell}>{purpose}</TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Public Description</TableCell>
                                <TableCell className={classes.tableCell}>{sendValues.PublicDescription ? sendValues.PublicDescription : '-'}</TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Private Description</TableCell>
                                <TableCell className={classes.tableCell}>{sendValues.PrivateDescription ? sendValues.PrivateDescription : '-'}</TableCell>
                            </TableRow>
                            <TableRow className={classes.tableRow}>
                                <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Timestamp</TableCell>
                                <TableCell className={classes.tableCell}>
                                    {fromApproval ?
                                        formatEpochDate((sendValues as Transaction).AuditTime)
                                        : formatEpochDate((sendValues as ISendFormValues).RequestTime)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
                {assetObj.Type === AssetTypes.ASSET ?
                    <Grid
                        className={classes.disclaimerContainer}
                        container
                        direction='column'
                        item
                        lg={4}
                        md={12}
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Typography
                            className={classes.disclaimerText}
                            variant='body1'
                        >
                            {`Please review the details of your transfer request, including the recipient and the amount of the shares of ${description} (${ticker}) you are transferring.`}
                        </Typography>
                        <Typography
                            className={classes.disclaimerText}
                            variant='body1'
                        >
                            <strong>
                                All transfer requests are final and you will not be able to reclaim
                                your shares after confirming the details of your transfer.
                            </strong>
                        </Typography>
                    </Grid>
                    : <></>}
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                alignItems='center'
                className={classes.submitContainer}
                container
                direction='column'
                item
                spacing={0}
            >
                <SubmitButton
                    onClick={() => {
                        handleSend(sendValues);
                        registerTimeout(onTimeoutError, 'send');
                    }}
                    type={fromApproval ? 'send-approve' : 'send-confirm'}
                />
                <Grid item>
                    <Typography
                        className={classes.cancelButton}
                        onClick={cancelConfirmation}
                        variant='h4'
                    >CANCEL
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default withStyles(muiStyles)(SendConfirmation);